/** Apples to Apples on a phone. Every control is `<PlayerInput>`; the rest is chrome. */

import { useEffect, useState } from 'react';
import { PlayerInput, Scoreboard, Timer, type GameUiProps } from '@gsf/kit';
import { applesAsks, applesView } from './view';

export function ApplesPlayer({ snapshot, submit, send, events, connected, onLeave }: GameUiProps) {
  const view = applesView(snapshot);
  const { you, shared, me } = view;
  // Only the party leader of an unlimited game can end it: there is no host and no
  // round count. `core:advanceSegment` finishes the segment.
  const canEndGame =
    you.isPartyLeader && shared.winCondition === 'unlimited' && shared.phase !== 'over';
  const asks = applesAsks(view);
  const [showScores, setShowScores] = useState(false);

  // Buzz the phone when a round is won; a phone in a pocket should feel the beat it missed.
  const winnerN = events.winner?.n;
  useEffect(() => {
    if (winnerN === undefined) return;
    navigator.vibrate?.([30, 50, 30]);
  }, [winnerN]);

  useEffect(() => {
    if (!showScores) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setShowScores(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showScores]);

  // `you` is core's and `private` is the game's; a snapshot always carries the first and
  // only carries the second once this device is a participant.
  if (!me || you.participantId === null) {
    return (
      <div className="centered">
        <p className="muted">Joining…</p>
      </div>
    );
  }

  const score = view.scoreOf(you.participantId);
  /**
   * The one ask aimed at this phone, in round order. `next` only exists at the reveal and
   * renders below the winning card.
   */
  const ask = asks.prompt ?? asks.play ?? asks.pick ?? asks.ready ?? asks.wait ?? asks.next;

  return (
    <div className="play">
      <div className="play-header">
        <span className="play-name">{you.name}</span>
        {view.iAmJudge ? <span className="tag tag-accent">judge</span> : null}
        {!connected ? <span className="tag tag-bad">offline</span> : null}
        <span className="play-score">{score}</span>
      </div>

      <div className="play-stage">
        {/* Shared, so a phone has it because the room does; the judge's candidates
            arrive on the request. */}
        {shared.green ? (
          <p className="apples-green apples-green-phone">{shared.green.text}</p>
        ) : null}

        {/* Core's play clock: the request is undeadlined and paired with a `timer` effect. */}
        {shared.phase === 'playing' ? (
          <Timer endsAt={view.core.deadlineAt} serverNow={view.core.serverNow} variant="phone" />
        ) : null}

        {/* At the reveal, both the winning card and the button that leaves it. */}
        {ask === null || shared.phase === 'reveal' ? <Stage view={view} /> : null}
        {ask ? <PlayerInput request={ask} onSubmit={submit} nameOf={view.nameOf} /> : null}

        {/* Which card of theirs is on the table, once the ask that took it has closed. */}
        {me.myPlay && (shared.phase === 'judging' || shared.phase === 'reveal') ? (
          <p className="play-hint">
            You played <strong>{me.myPlay.text}</strong>
          </p>
        ) : null}
      </div>

      <div className="play-footer">
        <button className="btn btn-sm" onClick={() => setShowScores(true)}>
          Green apples ({view.standings.length})
        </button>
        {canEndGame ? (
          <button
            className="btn btn-sm"
            title="End the game and show the final scores"
            onClick={() => {
              if (confirm('End the game now and show the final scores?')) {
                send('core:advanceSegment');
              }
            }}
          >
            End game
          </button>
        ) : null}
        <span className="spacer" />
        {onLeave ? (
          <button className="btn btn-sm btn-ghost" onClick={onLeave} title="Forget me and rejoin">
            leave
          </button>
        ) : null}
      </div>

      {showScores ? (
        <div className="sheet-backdrop" onClick={() => setShowScores(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-label="Green apples"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-head">
              <h2 className="panel-title" style={{ margin: 0 }}>
                Green apples
              </h2>
              <span className="spacer" />
              <button className="btn btn-sm" onClick={() => setShowScores(false)}>
                Close
              </button>
            </div>
            <Scoreboard
              players={view.standings}
              activeParticipantId={shared.judgeId}
              meId={you.participantId}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The chrome around the control, or all of it when there is none (segment end, between
 * rounds). At the reveal it sits above the ask.
 */
function Stage({ view }: { view: ReturnType<typeof applesView> }) {
  const { shared } = view;

  if (shared.phase === 'over') {
    return <p className="play-hint">That’s the game. Look at the TV for the winner.</p>;
  }

  if (shared.phase === 'reveal') {
    const winner = shared.winner;
    if (!winner) return <p className="play-hint">Nobody played that one. Next round…</p>;
    const name = view.nameOf(winner.participantId) ?? 'Somebody';
    const mine = winner.participantId === view.you.participantId;
    return (
      <>
        <p className={`play-status ${mine ? 'is-good' : 'is-wait'}`}>
          {mine ? 'You won it!' : `${name} won it`}
        </p>
        <p className="play-clue">{winner.text}</p>
      </>
    );
  }

  return <p className="play-hint">Look at the TV.</p>;
}
